import { caseMajorityOfCantonsVote } from '@/domain/federal-ballot-state'
import { FederalBallotSummary, toFederalBallotSummary } from '@/domain/FederalBallotSummary'
import { mkOutcome } from '@/domain/outcome'
import { OverrideableBallotState } from '@/domain/overrideable-ballot-state'
import { mkPopularVoteVisibility } from '@/domain/popular-vote-visibility'
import * as React from 'react'
import { css } from 'styled-components/macro'
import { useEnv, useFederalBallotDocument, useFirestoreSnapshot, usePopularVoteDocument } from '../../../lib'
import Tile from './Tile'
import * as firestore from 'firebase/firestore'

const Root = 'div'

interface Props extends React.ComponentPropsWithoutRef<typeof Root> {
  popularVoteId: string
  federalBallots: Array<{ id: string; name: string }>
}

function Tiles(props: Props) {
  const { popularVoteId, federalBallots, ...rest } = props

  return (
    <Root {...rest} css={styles.root as any}>
      {federalBallots.map((x) => (
        <div key={x.id} css={styles.tile as any}>
          <Tile_ popularVoteId={popularVoteId} federalBallotId={x.id} />
        </div>
      ))}
    </Root>
  )
}

const styles = {
  root: css`
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    overflow: hidden;

    margin: 0 -20px -80px;
  `,

  tile: css`
    width: 436px;

    display: grid;
    padding: 0 20px;

    margin-bottom: 80px;
  `,
}

export default Tiles

function Tile_(props: { popularVoteId: string; federalBallotId: string }) {
  const { db } = useEnv()

  const { popularVoteId, federalBallotId } = props

  const popularVoteDocument = usePopularVoteDocument({ popularVoteId })
  const federalBallotDocument = useFederalBallotDocument({
    popularVoteId,
    ballotId: federalBallotId,
  })
  const cantonsSnapshot = useFirestoreSnapshot(
    firestore.collection(db, 'popularVotes', popularVoteId, 'federalBallots', federalBallotId, 'cantons'),
  )

  const visibility = popularVoteDocument?.visibility ?? mkPopularVoteVisibility('inactive')

  const summary = React.useMemo((): undefined | FederalBallotSummary => {
    if (federalBallotDocument && cantonsSnapshot) {
      return toFederalBallotSummary({
        date: new Date(Date.parse(popularVoteId)),
        visibility,
        docSnapshot: {
          id: federalBallotId,
          data() {
            return federalBallotDocument
          },
        } as any,
        cantonsSnapshot,
      })
    } else {
      return undefined
    }
  }, [federalBallotDocument, cantonsSnapshot, popularVoteId, visibility, federalBallotId])

  const state = React.useMemo((): undefined | OverrideableBallotState => {
    if (summary) {
      return {
        ballotState: summary.state.ballotState,
        outcome: caseMajorityOfCantonsVote({
          notRequired: undefined,
          undecided: undefined,
          reject: mkOutcome('nay'),
          accept: undefined,
        })(summary.state.majorityOfCantons),
      }
    } else {
      return undefined
    }
  }, [summary])

  if (!state || !summary) {
    return null
  }

  return <Tile popularVoteId={popularVoteId} federalBallotId={federalBallotId} state={state} summary={summary} />
}
